import firebase from 'firebase';

// Store the original fetch function
const originalFetch = window.fetch;

// Response interceptor type
type ResponseInterceptor = (response: Response) => Response | Promise<Response>;
type ErrorInterceptor = (error: any) => Promise<any>;

class FetchInterceptor {
  private responseInterceptors: ResponseInterceptor[] = [];
  private errorInterceptors: ErrorInterceptor[] = [];

  // Add a response interceptor
  addResponseInterceptor(interceptor: ResponseInterceptor) {
    this.responseInterceptors.push(interceptor);
  }

  // Add an error interceptor
  addErrorInterceptor(interceptor: ErrorInterceptor) {
    this.errorInterceptors.push(interceptor);
  }

  // Initialize the interceptor
  init() {
    const self = this;
    
    // Override the global fetch
    window.fetch = async function(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      try {
        // Call the original fetch
        let response = await originalFetch(input, init);
        
        // Clone the response before processing (responses can only be read once)
        const clonedResponse = response.clone();
        
        // Run through response interceptors
        for (const interceptor of self.responseInterceptors) {
          try {
            response = await interceptor(response);
          } catch (interceptorError) {
            console.error('Response interceptor error:', interceptorError);
          }
        }
        
        // Check for common error scenarios
        if (response.status === 401) {
          // Token expired or invalid
          console.log('🔐 401 Unauthorized detected, handling token refresh...');
          
          const user = firebase.auth().currentUser;
          if (user) {
            try {
              // Force token refresh
              await user.getIdToken(true);
              
              // Retry the original request with new token
              const newToken = await user.getIdToken();
              
              // Update the authorization header
              const newInit = {
                ...init,
                headers: {
                  ...(init?.headers || {}),
                  'Authorization': `Bearer ${newToken}`
                }
              };
              
              // Retry the request
              response = await originalFetch(input, newInit);
              
              // Run through response interceptors again for the retry
              for (const interceptor of self.responseInterceptors) {
                try {
                  response = await interceptor(response);
                } catch (interceptorError) {
                  console.error('Response interceptor error on retry:', interceptorError);
                }
              }
            } catch (refreshError) {
              console.error('Token refresh failed:', refreshError);
              // If token refresh fails, sign out the user
              await firebase.auth().signOut();
              window.location.href = '/login';
            }
          }
        } else if (response.status === 402) {
          // Payment Required - Subscription not active
          try {
            const errorData = await clonedResponse.json();
            console.log('💳 Subscription required error detected:', errorData);
            
            // Dispatch a custom event that the app can listen to
            window.dispatchEvent(new CustomEvent('subscription-required', {
              detail: {
                message: errorData.message || 'An active subscription is required',
                subscriptionStatus: errorData.subscriptionStatus,
                currentPeriodEnd: errorData.currentPeriodEnd,
                error: errorData.error
              }
            }));
            
            // Store the subscription error in session storage for persistence
            sessionStorage.setItem('subscriptionError', JSON.stringify({
              timestamp: Date.now(),
              ...errorData
            }));
          } catch (e) {
            console.error('Failed to parse subscription error response:', e);
          }
        } else if (response.status === 403) {
          // Forbidden - could be other authorization issues
          try {
            const errorData = await clonedResponse.json();
            if (errorData.error === 'Subscription required' || errorData.message?.includes('subscription')) {
              console.log('📊 Subscription required error detected (403)');
              
              // Dispatch a custom event that the app can listen to
              window.dispatchEvent(new CustomEvent('subscription-required', {
                detail: {
                  message: errorData.message,
                  requiredPlan: errorData.requiredPlan
                }
              }));
            }
          } catch (e) {
            // If we can't parse the response, just continue
          }
        }
        
        return response;
      } catch (error) {
        // Run through error interceptors
        let processedError = error;
        for (const interceptor of self.errorInterceptors) {
          try {
            processedError = await interceptor(processedError);
          } catch (interceptorError) {
            console.error('Error interceptor error:', interceptorError);
          }
        }
        
        throw processedError;
      }
    };
  }
}

// Create and export a singleton instance
const fetchInterceptor = new FetchInterceptor();

// Add default interceptors
fetchInterceptor.addResponseInterceptor((response) => {
  // Log all API responses
  if (response.url.includes('/api/')) {
    console.log(`📡 API Response: ${response.status} ${response.url}`);
  }
  return response;
});

fetchInterceptor.addErrorInterceptor(async (error) => {
  // Log all errors
  console.error('🚨 Fetch Error:', error);
  
  // You can add more error handling here
  // For example, show a global error notification
  
  return Promise.reject(error);
});

export default fetchInterceptor;